import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLogin() {
  const { user, login, ready } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (ready && user && user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) nav("/admin", { replace: true });
    else setError(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6 py-12" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-terracotta text-white">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-ink">
            Admin sign in
          </h1>
          <p className="mt-2 text-brand-inkSoft text-sm">
            Internal area for Neighbours Brick &amp; Build staff.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="rounded-3xl border border-brand-line bg-white p-8 space-y-5"
        >
          <div>
            <Label htmlFor="email" className="text-brand-ink font-medium">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="admin-input-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="mt-2 h-12 rounded-xl bg-brand-bg border-brand-line focus-visible:ring-brand-terracotta"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-brand-ink font-medium">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-input-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="mt-2 h-12 rounded-xl bg-brand-bg border-brand-line focus-visible:ring-brand-terracotta"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full rounded-full h-12 bg-brand-terracotta hover:bg-brand-terracottaDark text-white"
          >
            {busy ? "Signing in…" : "Sign in"} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-brand-inkMuted">
          Lost your password? Speak to the office.
        </p>
      </div>
    </div>
  );
}
